import React, { useEffect, useState } from "react";
import axios from "axios";
import { apiKey } from "../config/config";
import Table from "../components/Table";

export default function ActivityLogReport({ lang = {} }) {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [filterPreset, setFilterPreset] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // const [loading, setLoading] = useState(false);

  const getAdminData = () => {
    try {
      const adminSession = sessionStorage.getItem("aman-admin");
      if (adminSession) {
        const parsedSession = JSON.parse(adminSession);
        const adminData = parsedSession?.admin;
        if (adminData) {
          return typeof adminData === "string" ? JSON.parse(adminData) : adminData;
        }
      }
      const userSession = sessionStorage.getItem("user");
      if (userSession) return JSON.parse(userSession);
    } catch (e) {
      console.error(e);
    }
    return {};
  };

  const getToken = () => {
    const admin = getAdminData();
    return admin.token || "";
  };

  const getBranchId = () => {
    const admin = getAdminData();
    return admin.branch_id || admin.id || "";
  };

  useEffect(() => {
    fetchUsers();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = () => {
    const branch_id = getBranchId();
    const token = getToken();
    if (!token) return;

    let url = `${apiKey}/activity-log/users`;
    if (branch_id) url += `?branch_id=${branch_id}`;

    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUsers(res.data.users || []);
      })
      .catch((err) => console.error(err));
  };

  const fetchLogs = (
    overrideFrom = fromDate,
    overrideTo = toDate,
    overridePreset = filterPreset,
    overrideUser = selectedUser,
    overrideAction = selectedAction
  ) => {
    const branch_id = getBranchId();
    const token = getToken();
    if (!token) return;

    let queryParams = [];
    if (branch_id) queryParams.push(`branch_id=${branch_id}`);
    if (overrideUser) queryParams.push(`user_id=${overrideUser}`);
    if (overrideAction) queryParams.push(`action_type=${encodeURIComponent(overrideAction)}`);
    if (overridePreset) queryParams.push(`filter=${overridePreset}`);
    if (overrideFrom) queryParams.push(`from=${overrideFrom}`);
    if (overrideTo) queryParams.push(`to=${overrideTo}`);

    const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    axios
      .get(`${apiKey}/activity-log/logs${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLogs(res.data.logs || []);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const handleClear = () => {
    setSelectedUser("");
    setSelectedAction("");
    setFilterPreset("");
    setFromDate("");
    setToDate("");
    fetchLogs("", "", "", "", "");
  };

  const columns = [
    {
      header: lang.SN || "SN",
      cell: (row) => row.row.index + 1,
    },
    {
      header: lang.Date_Time || "Date & Time",
      accessorKey: "created_at",
      cell: (row) => (row.getValue() ? new Date(row.getValue()).toLocaleString() : "N/A"),
    },
    {
      header: lang.Employee || "Employee / User",
      accessorKey: "employee_name",
    },
    {
      header: lang.Action_Type || "Action Type",
      accessorKey: "action_type",
    },
    {
      header: lang.Description || "Activity Description",
      accessorKey: "description",
      cell: (row) => <span className="small text-secondary">{row.getValue() || "N/A"}</span>,
    },
  ];

  return (
    <div className="container-fluid px-4 py-3">
      <div className="pagetitle mb-4">
        <h1 className="fw-bold">{lang.Activity_Logs || "Employee Activity Log Report"}</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
              >
                <path d="m21.743 12.331-9-10c-.379-.422-1.107-.422-1.486 0l-9 10a.998.998 0 0 0-.17 1.076c.16.361.518.593.913.593h2v7a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-4h4v4a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-7h2a.998.998 0 0 0 .743-1.669z"></path>
              </svg>
            </li>
            <li className="breadcrumb-item">{lang.Reports || "Reports"}</li>
            <li className="breadcrumb-item active">{lang.Activity_Logs || "Activity Logs"}</li>
          </ol>
        </nav>
      </div>

      {/* Filters Card */}
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-body p-3">
          <form onSubmit={handleFilterSubmit} className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-bold text-secondary">
                {lang.Employee || "Employee"}
              </label>
              <select
                className="form-select form-select-sm"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <option value="">All Employees</option>
                {users.map((u) => (
                  <option key={u.ID} value={u.ID}>
                    {u.FullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold text-secondary">{lang["Start Date"] || "Start Date"}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold text-secondary">{lang["End Date"] || "End Date"}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-2 d-flex gap-2">
              <button type="submit" className="btn btn-sm btn-primary fw-bold flex-grow-1">
                <i className="fa fa-filter me-1"></i> {lang["Filter"] || "Filter"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary fw-bold"
                onClick={handleClear}
              >
                {lang["Clear"] || "Clear"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Table Section */}
      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="row pt-3">
                <div className="rr col-9">
                  {lang.Activity_Logs || "Employee Activity Log History"}
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex mt-3 mb-3">
                  <Table
                    columns={columns}
                    data={logs}
                    lang={lang}
                    exportOption={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
